import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { findCategoryById, resolveCategoryId, formatPrice } from '@barter/shared';
import type { Ad } from '@barter/shared';
import WebApp from '@twa-dev/sdk';
import { photoUrl } from '../../services/api-base';
import { submitView } from '../../services/submit';
import { findSimilarAds } from '../../services/snapshot';
import {
  getAdById,
  addFavorite as addFavDb,
  removeFavorite as removeFavDb,
  isFavorite,
} from '../../db/database';
import {
  enqueueStatusChange,
  enqueueDelete,
  enqueueUpdate,
  enqueueCreate,
  enqueueReport,
} from '../../db/sync-queue';
import { ContactButton } from '../../components/ContactButton/ContactButton';
import { RequestPhone } from '../../components/RequestPhone/RequestPhone';
import { AdCard } from '../../components/AdCard/AdCard';
import { Icon } from '../../components/Icon/Icon';
import { useTelegramContext } from '../../context/TelegramContext';
import i18n from '../../i18n';
import { QRCodeSVG } from 'qrcode.react';

function formatDate(ts: number): string {
  const locale = i18n.language === 'ro' ? 'ro-RO' : i18n.language === 'ru' ? 'ru-RU' : 'uk-UA';
  return new Date(ts).toLocaleDateString(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AdDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useTelegramContext();
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [ad, setAd] = useState<Ad | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [statusLoading, setStatusLoading] = useState(false);
  const [reported, setReported] = useState(false);
  const [isFav, setIsFav] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const [similarAds, setSimilarAds] = useState<Ad[]>([]);
  const [showQr, setShowQr] = useState(false);
  const touchStartRef = useRef(0);

  const loadAd = useCallback(async () => {
    if (!id) return;
    const local = await getAdById(id);
    if (local) {
      setAd(local);
      setLoading(false);
      setError('');
    } else {
      setError(tRef.current('adDetails.notFound'));
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    isFavorite(id).then(setIsFav);
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    loadAd();

    // Счётчик просмотров — fire-and-forget в Worker
    if (navigator.onLine) submitView(id);
  }, [id, loadAd]);

  // Обновление, когда подтянулся новый снапшот
  useEffect(() => {
    const onNewAds = () => loadAd();
    window.addEventListener('barter:new-ads', onNewAds);
    return () => window.removeEventListener('barter:new-ads', onNewAds);
  }, [loadAd]);

  // Похожие объявления считаются локально из Dexie
  useEffect(() => {
    if (!id) return;
    getAdById(id).then((current) => {
      if (current) findSimilarAds(current, 5).then(setSimilarAds);
    });
  }, [id, ad?.updatedAt]);

  if (loading) return <div className="loading-state">{t('adDetails.loading')}</div>;
  if (error) return <div className="error-state">{error}</div>;
  if (!ad) return <div className="error-state">{t('adDetails.notFound')}</div>;

  const category = findCategoryById(resolveCategoryId(ad.categoryId));
  const isOwner = user && user.id === ad.sellerTelegramId;

  const handleSwipe = (e: React.TouchEvent) => {
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && currentPhoto < ad.photos.length - 1) setCurrentPhoto((p) => p + 1);
      if (diff < 0 && currentPhoto > 0) setCurrentPhoto((p) => p - 1);
    }
  };

  const handleStatusChange = async (status: Ad['status']) => {
    if (!ad) return;
    setStatusLoading(true);
    try {
      const updated = { ...ad, status, updatedAt: Date.now() };
      setAd(updated);
      await enqueueStatusChange(ad.id, status);
    } catch {
      alert(t('adDetails.statusError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!ad) return;
    if (!window.confirm(t('adDetails.confirmDelete'))) return;
    try {
      await enqueueDelete(ad.id);
      navigate('/');
    } catch {
      alert(t('adDetails.deleteError'));
    }
  };

  const handleReport = async () => {
    if (!ad || reported) return;
    if (!window.confirm(t('adDetails.confirmReport'))) return;
    try {
      await enqueueReport(ad.id, 'other');
      setReported(true);
    } catch {
      alert(t('adDetails.reportError'));
    }
  };

  const handleToggleFavorite = async () => {
    if (!ad) return;
    setFavLoading(true);
    try {
      if (isFav) {
        await removeFavDb(ad.id);
        setIsFav(false);
      } else {
        await addFavDb(ad.id);
        setIsFav(true);
      }
    } catch {
    } finally {
      setFavLoading(false);
    }
  };

  const handleRenew = async () => {
    if (!ad) return;
    if (!window.confirm(t('adDetails.renewConfirm'))) return;
    setStatusLoading(true);
    try {
      const updated = { ...ad, createdAt: Date.now(), updatedAt: Date.now() };
      setAd(updated);
      await enqueueUpdate(ad.id, { renew: true });
    } catch {
      alert(t('adDetails.renewError'));
    } finally {
      setStatusLoading(false);
    }
  };

  // Дублирование делаем на клиенте: новое объявление с теми же данными
  // (фото переиспользуем по уже опубликованным ключам)
  const handleDuplicate = async () => {
    if (!ad) return;
    if (!window.confirm(t('adDetails.duplicateConfirm'))) return;
    setStatusLoading(true);
    try {
      const tempAd: Ad = {
        ...ad,
        id: crypto.randomUUID(),
        status: 'active',
        views: 0,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      await enqueueCreate(
        {
          title: ad.title,
          description: ad.description,
          price: ad.price,
          currency: ad.currency,
          categoryId: ad.categoryId,
          condition: ad.condition,
          wanted: ad.wanted,
          photos: ad.photos,
          contacts: ad.contacts,
          location: ad.location,
        },
        tempAd,
      );
      navigate(`/ad/${tempAd.id}`);
    } catch {
      alert(t('adDetails.duplicateError'));
    } finally {
      setStatusLoading(false);
    }
  };

  const handleShare = () => {
    const text = `${ad.title} — ${ad.price != null ? ad.price + ' ' + ad.currency : t('adDetails.negotiable')}`;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: ad.title, text, url }).catch(() => {});
    } else {
      try {
        WebApp.openTelegramLink(
          `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
        );
      } catch {
        navigator.clipboard.writeText(`${text}\n${url}`).catch(() => {});
      }
    }
  };

  return (
    <div className="offer-details">
      {ad.photos.length > 0 && (
        <div
          className="carousel"
          onTouchStart={(e) => {
            touchStartRef.current = e.touches[0].clientX;
          }}
          onTouchEnd={handleSwipe}
        >
          <img src={photoUrl(ad.photos[currentPhoto]!)} alt={ad.title} className="carousel__img" />
          {ad.photos.length > 1 && (
            <>
              {currentPhoto > 0 && (
                <button
                  className="carousel__btn carousel__btn--prev"
                  onClick={() => setCurrentPhoto((p) => p - 1)}
                  aria-label={t('common.prevPhoto')}
                  type="button"
                >
                  ‹
                </button>
              )}
              {currentPhoto < ad.photos.length - 1 && (
                <button
                  className="carousel__btn carousel__btn--next"
                  onClick={() => setCurrentPhoto((p) => p + 1)}
                  aria-label={t('common.nextPhoto')}
                  type="button"
                >
                  ›
                </button>
              )}
            </>
          )}
          <div className="carousel__counter">
            {currentPhoto + 1} / {ad.photos.length}
          </div>
          {ad.photos.length > 1 && (
            <div className="carousel__dots">
              {ad.photos.map((_, i) => (
                <button
                  key={i}
                  className={`carousel__dot${i === currentPhoto ? ' carousel__dot--active' : ''}`}
                  onClick={() => setCurrentPhoto(i)}
                  aria-label={`${t('common.photo')} ${i + 1}`}
                  type="button"
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="offer-details__header-row">
        <div className="offer-details__price">
          {formatPrice(ad.price, ad.currency, t('adDetails.negotiable'))}
        </div>
        {!isOwner && (
          <button
            className={`btn btn--small btn--fav${isFav ? ' btn--fav-active' : ''}`}
            onClick={handleToggleFavorite}
            disabled={favLoading}
          >
            {isFav ? t('adDetails.favoriteRemove') : t('adDetails.favoriteAdd')}
          </button>
        )}
      </div>
      <h1 className="offer-details__title">{ad.title}</h1>

      <div className="offer-details__meta">
        {category && (
          <span className="offer-details__category">
            {category.icon} {t(category.nameKey ?? category.id)}
          </span>
        )}
        {ad.condition && (
          <span className={`badge badge--${ad.condition}`}>
            {t(ad.condition === 'new' ? 'adDetails.conditionNew' : 'adDetails.conditionUsed')}
          </span>
        )}
        {(ad.make || ad.model) && (
          <span className="offer-details__category">
            <Icon name="car" size={14} /> {[ad.make, ad.model].filter(Boolean).join(' ')}
          </span>
        )}
        {ad.location && (
          <span>
            <Icon name="location" size={14} /> {ad.location.city}
          </span>
        )}
        <span>
          <Icon name="calendar" size={14} /> {formatDate(ad.createdAt)}
        </span>
        {ad.views > 0 && (
          <span>
            <Icon name="eye" size={14} /> {ad.views} {t('adDetails.views')}
          </span>
        )}
      </div>

      {ad.wanted && (
        <div className="offer-details__wanted">
          <Icon name="repeat" size={15} /> <strong>{t('adDetails.wantedTitle')}:</strong> {ad.wanted}
        </div>
      )}

      <p className="offer-details__description">{ad.description}</p>

      <div className="offer-details__seller">
        <span className="offer-details__seller-name">
          {t('adDetails.seller', { name: ad.sellerName })}
        </span>
      </div>

      {!isOwner && (
        <>
          <ContactButton contacts={ad.contacts} sourceUrl={ad.sourceUrl} />
          {!ad.contacts.phone && ad.sourceUrl && (
            <RequestPhone
              adId={ad.id}
              sourceUrl={ad.sourceUrl}
              onPhone={(phone) =>
                setAd((prev) =>
                  prev ? { ...prev, contacts: { ...prev.contacts, phone } } : prev,
                )
              }
            />
          )}
        </>
      )}

      <div className="offer-details__actions-row">
        {!isOwner && (
          <button className="btn btn--small btn--share" onClick={handleShare}>
            {t('adDetails.share')}
          </button>
        )}
        <button className="btn btn--small" onClick={() => setShowQr(!showQr)}>
          {t('adDetails.qr')}
        </button>
        {!isOwner && (
          <div className="offer-details__report">
            <button
              className="btn btn--small btn--danger"
              onClick={handleReport}
              disabled={reported}
            >
              {reported ? t('adDetails.reported') : t('adDetails.report')}
            </button>
          </div>
        )}
      </div>

      {showQr && (
        <div
          className="offer-details__qr"
          onClick={() => setShowQr(false)}
          onKeyDown={(e) => e.key === 'Escape' && setShowQr(false)}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <div className="offer-details__qr-inner" onClick={(e) => e.stopPropagation()}>
            <QRCodeSVG value={window.location.href} size={200} />
            <p className="offer-details__qr-hint">{ad.title}</p>
          </div>
        </div>
      )}

      {isOwner && (
        <div className="offer-details__owner-actions">
          <div className={`status-badge status-badge--${ad.status}`}>
            {ad.status === 'active'
              ? t('adDetails.active')
              : ad.status === 'sold'
                ? t('adDetails.sold')
                : t('adDetails.archived')}
          </div>
          <div className="offer-details__status-buttons">
            <button
              className="btn btn--small btn--primary"
              onClick={() => navigate(`/edit/${ad.id}`)}
            >
              {t('adDetails.editBtn')}
            </button>
            {ad.status === 'active' && (
              <button className="btn btn--small" onClick={handleRenew} disabled={statusLoading}>
                {t('adDetails.renew')}
              </button>
            )}
            <button className="btn btn--small" onClick={handleDuplicate} disabled={statusLoading}>
              {t('adDetails.duplicate')}
            </button>
            {ad.status !== 'active' && (
              <button
                className="btn btn--small"
                onClick={() => handleStatusChange('active')}
                disabled={statusLoading}
              >
                {t('adDetails.activate')}
              </button>
            )}
            {ad.status !== 'sold' && (
              <button
                className="btn btn--small"
                onClick={() => handleStatusChange('sold')}
                disabled={statusLoading}
              >
                {t('adDetails.markSold')}
              </button>
            )}
            {ad.status !== 'archived' && (
              <button
                className="btn btn--small"
                onClick={() => handleStatusChange('archived')}
                disabled={statusLoading}
              >
                {t('adDetails.toArchive')}
              </button>
            )}
            <button className="btn btn--small btn--danger" onClick={handleDelete}>
              {t('adDetails.delete')}
            </button>
          </div>
        </div>
      )}

      {similarAds.length > 0 && (
        <div className="offer-details__similar">
          <h3 className="section-title">{t('adDetails.similar')}</h3>
          <div className="offer-list">
            {similarAds.map((s) => (
              <AdCard key={s.id} ad={s} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
